import { useCallback, useEffect, useId, useRef, useState, type CSSProperties, type JSX } from 'react'
import {
  ApiError, SESSION_ENDED, api,
  type CharacterizationImpact, type CharacterizationQueueRow, type ReviewDecision,
  type ReviewOutcome,
} from '../api.js'
import { ConfidencePill, asWords, formatDate, formatKw, formatMoney } from '../components/Pill.js'
import { T } from '../tokens.js'

/**
 * The analyst's characterization review queue (R-24, R-25, R-26, brief §4.6) —
 * the wireframe's artboard **1g**, *"Step 4 · Characterization review queue —
 * analyst"*.
 *
 * ## The decision is made against its consequence, not against how it reads
 *
 * An analyst is deciding whether a claim about a building is true, and
 * approving it **re-scores that account**. A surface that showed the proposal
 * and not its consequence would invite approval-by-plausibility: the claim
 * reads sensibly, so it is waved through, and an estimate moves for a reason
 * nobody examined. The largest such move measured on the seeded book is
 * 129.1 kW → 64.8 kW on one approval. So the impact panel is not an extra; it
 * is why the screen has a right-hand side at all.
 *
 * ## Two measured facts shape the panel, and both are counter-intuitive
 *
 * **A kW-only panel would show nothing on the wireframe's own case.** Approving
 * an `undetermined` proposal confers R-26's confidence uplift with a kW delta
 * of exactly **0**, because `factors.ts` normalises `'undetermined'` to `null`
 * for every lookup. A screen that only drew kW movement would draw a blank and
 * imply that nothing happens — when what happens is that the account leaves the
 * queue and the confidence label may move. Hence three cells, of which only the
 * first is kW.
 *
 * **The same approval moves an account 129.1 → 64.8 kW in July and not at all
 * in September**, because occupancy calendars are seasonal and the preview is
 * scored as of the latest completed run's date. So {@link ImpactCells} prints
 * that date above the figures rather than beside them in small type: a delta
 * whose date is off the screen is a delta a reviewer cannot check.
 *
 * ## One impact at a time, and that is a budget rather than a preference
 *
 * `GET /api/characterizations/:id/impact` is two `scoreAccount` calls —
 * **207 ms, measured**. The wireframe's queue opens on 31 rows; previewing them
 * eagerly is ~6.4 s of synchronous better-sqlite3 work before anything renders,
 * and an analyst reads one proposal at a time. So the queue route sends rows
 * with no impacts and this screen fetches exactly one, for the selected row.
 * `ReviewQueue.test.tsx` counts the impact requests after a three-row mount for
 * that reason — it is the assertion an eager implementation fails.
 *
 * ## Approving `undetermined` is a first-class outcome
 *
 * It means *somebody looked and could not tell*. It removes the account from
 * the queue and asserts no building type, and display rule 3 exists because
 * collapsing it into "still uncharacterized" loses the distinction that decides
 * whether anyone looks again. The action is drawn exactly as available and as
 * unembarrassed as approving a real type — no warning, no confirmation, no
 * second-guessing copy — and the panel says in words what the approval means.
 *
 * ## Empty is Tuesday
 *
 * Most accounts will never have a proposal (R-26): of the seeded book's 313
 * in-scope accounts, 212 have never been looked at, and a queue built as
 * "accounts missing a characterization" would open with 212 rows of nothing
 * wrong. An empty queue is not a backlog cleared and not a problem, so
 * {@link QUEUE_EMPTY} congratulates nobody and blames nobody, and the empty
 * state is not an `alert`.
 *
 * ## Where the amend vocabulary comes from, and where it does not
 *
 * **No route serves an analyst the configured building types.** `GET
 * /api/config/at` carries `buildingTypes` and is `requireRole('program_manager')`;
 * `GET /api/config/current` is `requireAuth` but is a deliberately narrow
 * projection — season, window, events, rates — and names no vocabulary. So the
 * amendment field is a text box against the server's own validation rather
 * than a select, and a refused type comes back as
 * `proposeCharacterization`'s 400, which **enumerates the declared types**;
 * this screen renders that message verbatim.
 *
 * The alternative was a list of the six types hardcoded here, and that is the
 * one thing this codebase keeps refusing to ship: `Pill.tsx`'s `asWords`
 * docblock makes the argument, and it applies with more force to a control that
 * *writes*. A copy of the table would be wrong the first time somebody edited
 * the configuration and right about nothing in the meantime. The field is
 * pre-filled with the proposed type, so the common case — a small correction —
 * starts from a valid id in the right shape.
 *
 * That gap is a finding rather than a design: the honest fix is a rep-and-
 * analyst-facing route for the building-type vocabulary, and there is none.
 *
 * ## What this screen does not do
 *
 * It does not score, it does not diff, and it does not decide whether a reason
 * is a reason. `review.ts` requires one to amend or reject and refuses `''`,
 * and the disabled buttons here are a courtesy in front of that rule rather
 * than a second copy of it — which is why they are disabled on
 * `reason.trim() === ''` exactly, and why the server still refuses if they are
 * bypassed.
 */

export const REVIEW_HEADING = 'Characterization review'

/**
 * Zero proposals waiting.
 *
 * Deliberately free of *all caught up*, *backlog*, *outstanding* and *overdue*.
 * Every one of those words describes a queue somebody is behind on, and this
 * one is not: research reaches it only when a rep files some, which most
 * accounts never will (R-26). The sentence says where rows come from, because
 * that is the only thing a reader of an empty queue does not already know.
 */
export const QUEUE_EMPTY
  = 'No proposals are waiting. Research arrives here when somebody files it about an account, '
  + 'which is not something most accounts ever need.'

/**
 * The 403. Every route this screen opens on is `requireRole('analyst')`, so a
 * program manager or a rep who typed the URL gets a sentence about whose queue
 * this is rather than "try again", which would send them to retry something
 * that will never work. The app bar draws the tab disabled with the same fact,
 * so this is the deep-link path only.
 */
export const REVIEW_FORBIDDEN
  = 'Characterization review is an analyst’s queue. Your account cannot decide what has been '
  + 'filed about an account.'

export const QUEUE_FAILED = 'The review queue could not be loaded. Try again.'

export const IMPACT_FAILED = 'The estimate impact could not be computed for this proposal.'

/**
 * The 409 from `NoScoredBookError`: no run has completed, so there is no scored
 * book to compare an approval against.
 *
 * Not a failure and not a reason to block the decision. `review.ts` records the
 * decision either way and reports `rescored: false`, and the next run picks the
 * approval up through `startRun`'s pin like any other — so the sentence says
 * that rather than implying the analyst should wait.
 */
export const NO_SCORED_BOOK
  = 'No scoring run has completed yet, so there is no estimate to compare an approval against. '
  + 'Deciding still records the decision, and the next run picks it up.'

/**
 * The amendment field's hint, and it says the box may not be left empty.
 *
 * Not a restatement of a server rule, because there is none to restate.
 * `review.ts`'s `amend` reads an absent `buildingType` as *carry the rep's
 * original across* — correctly, since an amendment that corrects only a
 * footprint must not blank the type — so a cleared box was accepted, rejected
 * the rep's revision, re-filed the identical type, and reported "Amended and
 * approved as revision N". This form offers no field but the type, so an empty
 * box here is an empty amendment and the control refuses it.
 */
export const AMENDMENT_NEEDS_A_TYPE
  = 'The configured type’s id, as the program spells it. An unknown one comes back refused, with '
  + 'the list. It cannot be left empty: an amendment that changes no type is the revision you are '
  + 'rejecting, filed again.'

/** The wireframe's own words for the case where the proposed type reaches no configuration. */
export const INDUSTRY_ROW_GOVERNS = 'No change — the industry row still governs.'

/**
 * The third cell's subtext, and the one place this screen departs from the
 * wireframe on purpose.
 *
 * 1g draws *"Won't resurface for 12 months"*. **There is no such field and no
 * such mechanism.** `characterizations` carries no review-interval column,
 * nothing schedules a re-review, and `reviewQueue` selects `state = 'proposed'`
 * and nothing else — so a decided row leaves the queue permanently and returns
 * only when somebody files a new proposal. Printing a twelve-month promise the
 * system cannot keep would be worse than dropping the cell; dropping the cell
 * would lose the one thing an approved `undetermined` actually does, which is
 * the wireframe's own case. So the cell stays and says what is true.
 */
export const NO_RESURFACING
  = 'Nothing schedules a second look. The account returns only if somebody files new research.'

/** The wireframe's hand-note, and display rule 3's whole point. */
export const UNDETERMINED_IS_A_FINDING
  = '“Somebody looked and could not tell” is a finding, not a blank. Approving it takes the '
  + 'account out of the research queue without pretending to a building type.'

/**
 * Brief §4.6, said where the reject button is rather than after it is pressed —
 * and saying only the half that is true today.
 *
 * The obvious sentence is *"…and stay visible on the account"*, which is what
 * the brief asks for and what the plan's Task 8 states. **It is not true of any
 * shipped screen.** `GET /api/accounts/:id/characterizations` returns the whole
 * record, rejections included, and nothing in this package calls it: the
 * drill-down renders `characterization.provenance` out of the detail payload,
 * which is the *approved* row alone. So this promises what `store.ts` actually
 * guarantees — nothing is deleted, nothing is overwritten, and a rejection
 * keeps its version and its reason — and stops there. The account-history
 * surface is a gap, recorded in the plan file, not something this screen can
 * assert its way into existing.
 */
export const REJECTIONS_ARE_RETAINED
  = 'Rejections are retained with their reason — nothing here deletes and nothing overwrites.'

/**
 * What an approval's re-score does and does not move.
 *
 * `review.ts` writes the new estimate with `run_id = null` on purpose: giving
 * the approval a one-account run of its own would make it the latest completed
 * run, and `queryAccounts` would then rank a targeting list of one account
 * while `loadCurrentEstimate` answered `undefined` for the other 312. So the
 * drill-down shows the re-scored figure and the list keeps the run's number
 * until the next run absorbs it. An analyst who is not told that will report
 * the list as broken.
 */
export const RESCORE_IS_NOT_THE_LIST
  = 'The account page shows the new figure. The targeting list keeps the run’s number until the '
  + 'next scoring run.'

type Load =
  | { phase: 'loading' }
  /**
   * The 401, which was reaching the `failed` branch and its "Try again".
   *
   * `ApiError.sessionEnded` has existed since `api.ts` was written and had no
   * reader anywhere in `packages/web`: this screen and the console both branched
   * on `status === 403` alone, so an expired session rendered a retry prompt for
   * the one condition retrying cannot fix.
   */
  | { phase: 'signedOut' }
  | { phase: 'forbidden' }
  | { phase: 'failed' }
  | { phase: 'ready'; queue: CharacterizationQueueRow[] }

type Impact =
  | { phase: 'idle' }
  | { phase: 'running' }
  | { phase: 'ready'; impact: CharacterizationImpact }
  | { phase: 'failed'; message: string }

/** Which decision the analyst has opened a form for. `null` is "none yet". */
type Pending = null | 'amend' | 'reject'

const microLabel: CSSProperties = {
  fontSize: T.micro.size,
  letterSpacing: T.micro.tracking,
  textTransform: T.micro.transform,
  fontWeight: T.weight.bold,
  color: T.color.micro,
}

const buttonBase: CSSProperties = {
  border: `1.5px solid ${T.color.ink}`,
  background: '#fff',
  color: T.color.ink,
  padding: '10px 14px',
  fontFamily: T.fontFamily,
  fontSize: T.micro.size,
  fontWeight: T.weight.bold,
  letterSpacing: '0.08em',
  textTransform: T.micro.transform,
  borderRadius: T.radius.button,
  cursor: 'pointer',
}

const primaryButton: CSSProperties = {
  ...buttonBase,
  background: T.color.ink,
  color: '#fff',
}

const fieldStyle: CSSProperties = {
  border: `1.5px solid ${T.color.border}`,
  borderRadius: T.radius.button,
  padding: '9px 10px',
  fontFamily: T.fontFamily,
  fontSize: 13,
  color: T.color.ink,
  width: '100%',
  boxSizing: 'border-box',
}

/**
 * The proposed type as words — and `undetermined` as the single word, which is
 * the one place this screen says something shorter than `Pill.tsx` does.
 *
 * `UNDETERMINED_LABEL` spells out *"Undetermined — someone looked and could not
 * tell"* because on the drill-down and the targeting list the middle state has
 * to be told apart from *"nobody has looked"*, and there is nothing else on
 * those surfaces to do it. **Here the two cannot be confused**: every row in
 * this queue is a proposal by construction (`reviewQueue` selects
 * `state = 'proposed'`), so "no row at all" is not a state this screen can
 * render. The distinction is instead carried by the amber, by the panel's own
 * {@link UNDETERMINED_IS_A_FINDING} note, and by the fact that somebody is
 * looking at a piece of research about it.
 *
 * The long label also wrapped to three lines in a 288px rail and read as a
 * sentence rather than a value, which is the opposite of what display rule 3
 * wants for it.
 */
function typeLabel(buildingType: string): string {
  return asWords(buildingType)
}

/** `+3.2 kW` / `−64.3 kW`, with a real minus sign rather than a hyphen. */
function signedKw(delta: number): string {
  return `${delta < 0 ? '−' : '+'}${formatKw(Math.abs(delta))}`
}

/**
 * What the kW cell says underneath the two figures.
 *
 * **A zero has two different meanings and they are not interchangeable.** If
 * the proposed type resolves no configuration of its own — no building-type
 * factor row, no building-type end-use mix, no occupancy calendar in effect on
 * this date — then approving it *cannot* move a kW figure, structurally, and
 * the wireframe's "the industry row still governs" is a statement about the
 * document. If it does resolve something and the figure still does not move,
 * that is a fact about this account on this date (`fallbackProfile` applies no
 * occupancy calendar; a calendar that says "full campus" in September moves
 * nothing in September) and saying "the industry row governs" would be false.
 *
 * A reviewer looking at `88 → 88` with no explanation is entitled to conclude
 * the preview is broken, which is why neither case is allowed to render blank.
 */
function kwNote(impact: CharacterizationImpact): string {
  if (impact.delta.kw !== 0) return `${signedKw(impact.delta.kw)} on this approval.`
  const { factors, endUseMix, occupancy } = impact.reach
  if (!factors && !endUseMix && !occupancy) return INDUSTRY_ROW_GOVERNS
  return `No change — ${typeLabel(impact.proposedBuildingType)} carries configuration of its own, `
    + `but none of it moves this account on ${formatDate(impact.asOfDate)}.`
}

const METHOD_WORDS: Record<'decomposition' | 'fallback', string> = {
  decomposition: 'Decomposition',
  fallback: 'Fallback',
}

/** What the confidence cell says underneath the two chips. */
function confidenceNote(impact: CharacterizationImpact): string {
  const { current, ifApproved, delta } = impact
  if (delta.characterizationContributed) {
    return 'Raised because an approved characterization now backs a catch-all industry code (R-26).'
  }
  if (current.method !== ifApproved.method) {
    return `Method changes from ${METHOD_WORDS[current.method].toLowerCase()} to `
      + `${METHOD_WORDS[ifApproved.method].toLowerCase()}.`
  }
  if (delta.confidenceFrom === delta.confidenceTo) {
    return `${METHOD_WORDS[current.method]} method unchanged.`
  }
  return 'The label moves because the estimate does.'
}

interface CellProps {
  label: string
  testId: string
  children: JSX.Element | (JSX.Element | string)[]
  note: string
  /** The rightmost cell carries no rule of its own; the panel's border is already there. */
  last?: boolean
}

function Cell({ label, testId, children, note, last = false }: CellProps): JSX.Element {
  return (
    <div
      data-testid={testId}
      style={{
        padding: 12,
        borderRight: last ? 0 : `1px solid ${T.color.hairline}`,
        minWidth: 0,
      }}
    >
      <div style={microLabel}>{label}</div>
      <div style={{ marginTop: 6, fontSize: 15, fontWeight: T.weight.bold }}>{children}</div>
      <div style={{ marginTop: 5, fontSize: 12.5, color: T.color.muted, lineHeight: 1.45 }}>{note}</div>
    </div>
  )
}

/**
 * The wireframe's three cells, and the date they were all computed on.
 *
 * The date is above the grid rather than inside one cell because it qualifies
 * all three: both sides are scored as of the latest completed run's `as_of_date`
 * against that run's stamped configuration version (R-22), so it is the
 * *comparison* that is dated, not one figure in it.
 */
function ImpactCells({ impact }: { impact: CharacterizationImpact }): JSX.Element {
  return (
    <>
      <div
        data-testid="impact-as-of"
        data-as-of={impact.asOfDate}
        data-config-version={impact.configVersionId}
        style={{ marginTop: 6, fontSize: 12.5, color: T.color.muted }}
      >
        {`Both figures scored as of ${formatDate(impact.asOfDate)}, against the configuration the `
          + `run was stamped with (${impact.configVersionId}).`}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          marginTop: 10,
          border: `1.5px solid ${T.color.ink}`,
        }}
      >
        <Cell label="Curtailable kW" testId="impact-kw" note={kwNote(impact)}>
          <>
            <span data-testid="impact-kw-figures">
              {`${formatKw(impact.current.curtailableKwRaw)} → ${formatKw(impact.ifApproved.curtailableKwRaw)}`}
            </span>
            <div
              data-testid="impact-season-value"
              style={{ marginTop: 4, fontSize: 13, fontWeight: T.weight.body, color: T.color.money }}
            >
              {`${formatMoney(impact.current.seasonValueTotal)} → ${formatMoney(impact.ifApproved.seasonValueTotal)}`}
            </div>
          </>
        </Cell>
        <Cell label="Confidence" testId="impact-confidence" note={confidenceNote(impact)}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
            <ConfidencePill confidence={impact.delta.confidenceFrom} />
            <span aria-hidden="true">→</span>
            <ConfidencePill confidence={impact.delta.confidenceTo} />
          </span>
        </Cell>
        {/*
          The third cell is the one an `undetermined` approval is actually
          about, and the one the wireframe backs with a number this system does
          not have. See {@link NO_RESURFACING}.
        */}
        <Cell label="Research queue" testId="impact-queue" note={NO_RESURFACING} last>
          <span>Removed</span>
        </Cell>
      </div>
    </>
  )
}

export function ReviewQueue(): JSX.Element {
  const [load, setLoad] = useState<Load>({ phase: 'loading' })
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [impact, setImpact] = useState<Impact>({ phase: 'idle' })
  const [pending, setPending] = useState<Pending>(null)
  const [reason, setReason] = useState('')
  const [amendType, setAmendType] = useState('')
  const [busy, setBusy] = useState(false)
  const [outcome, setOutcome] = useState<string | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  const reasonId = useId()
  const typeId = useId()

  /**
   * `alive` and `era` together, for `ConfigConsole`'s reason: a reply that
   * arrives after the component is gone must not set state, and a reply for a
   * proposal the analyst has already moved off must not overwrite the one they
   * are looking at. The second is the one that bites — 207 ms is long enough to
   * click twice.
   *
   * `alive` is re-armed on mount, not merely disarmed on unmount. `main.tsx`
   * renders in `<StrictMode>`, which double-invokes mount effects in
   * development: the cleanup runs once before the second mount, and an effect
   * that only ever sets `false` leaves the guard permanently closed. Every
   * continuation below is then dropped — including `.finally`'s `setBusy(false)`,
   * which is what sticks all three decision buttons disabled after one click.
   * Every other component in this package re-arms it; this one did not.
   */
  const alive = useRef(true)
  const era = useRef(0)
  useEffect(() => {
    alive.current = true
    return () => { alive.current = false }
  }, [])

  useEffect(() => {
    let cancelled = false
    void api.reviewQueue()
      .then((queue) => {
        if (cancelled) return
        setLoad({ phase: 'ready', queue })
        setSelectedId(queue[0]?.id ?? null)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        // Three refusals, three sentences. A 401 is an expired session and the
        // answer is to sign in again; a 403 is `requireRole('analyst')` and the
        // answer is whose queue this is; anything else is worth retrying.
        // "Try again" was what a 401 got, and it is the one that never works.
        //
        // `sessionEnded` rather than `status === 401` — `api.ts` owns that
        // mapping, and a second copy of it is how the two come apart.
        if (error instanceof ApiError && error.sessionEnded) setLoad({ phase: 'signedOut' })
        else if (error instanceof ApiError && error.status === 403) setLoad({ phase: 'forbidden' })
        else setLoad({ phase: 'failed' })
      })
    return () => { cancelled = true }
  }, [])

  /**
   * One impact, for the selected row, on selection — never for the queue.
   *
   * This is the whole of the 207 ms budget's enforcement, and it is an effect
   * keyed on `selectedId` rather than a call inside the click handler so that
   * the row selected on load is previewed by the same code path as one clicked
   * later. Two paths is how the first row ends up the only one nobody measured.
   */
  useEffect(() => {
    if (selectedId === null) {
      setImpact({ phase: 'idle' })
      return
    }
    const mine = era.current + 1
    era.current = mine
    setImpact({ phase: 'running' })
    void api.characterizationImpact(selectedId)
      .then((result) => {
        if (!alive.current || era.current !== mine) return
        setImpact({ phase: 'ready', impact: result })
      })
      .catch((error: unknown) => {
        if (!alive.current || era.current !== mine) return
        const noBook = error instanceof ApiError && error.code === 'no_scored_book'
        setImpact({ phase: 'failed', message: noBook ? NO_SCORED_BOOK : IMPACT_FAILED })
      })
  }, [selectedId])

  const select = useCallback((id: string) => {
    setSelectedId(id)
    // Every part of the decision form belongs to the proposal it was opened on.
    // Carrying a half-typed rejection reason onto the next account is how a
    // reason ends up filed against research it was never about.
    setPending(null)
    setReason('')
    setAmendType('')
    setProblem(null)
    setOutcome(null)
  }, [])

  const queue = load.phase === 'ready' ? load.queue : []
  const selected = queue.find((row) => row.id === selectedId) ?? null

  /** What a landed decision says it did — from the outcome, never from hope. */
  const outcomeSentence = (
    decision: ReviewDecision, row: CharacterizationQueueRow, result: ReviewOutcome,
  ): string => {
    if (decision === 'reject') {
      return `Rejected — ${row.accountName}. ${REJECTIONS_ARE_RETAINED}`
    }
    const what = decision === 'amend'
      ? `Amended and approved as revision ${result.version} of ${row.accountName}. `
        + 'The original is retained as rejected, with your reason on both.'
      : `Approved — ${row.accountName}.`
    return result.rescored
      ? `${what} Re-scored. ${RESCORE_IS_NOT_THE_LIST}`
      : `${what} No run has completed, so nothing was re-scored; the next run picks it up.`
  }

  const decide = (decision: ReviewDecision): void => {
    if (selected === null || busy) return
    const row = selected
    setBusy(true)
    setProblem(null)
    void api.decideCharacterization(row.id, {
      decision,
      ...(decision === 'approve' ? {} : { reason }),
      /*
        An amendment always carries a type, and the button below refuses to
        fire without one. Omitting the field is not "leave it alone": `amend`
        in `review.ts` treats an absent `buildingType` as *carry the original
        across*, so clearing this box rejected the rep's revision and re-filed
        the identical type as a new approved one — and told the analyst
        "Amended and approved as revision N" for a revision that differs from
        the one they just rejected in nothing at all.
      */
      ...(decision === 'amend' ? { buildingType: amendType.trim() } : {}),
    })
      .then((result) => {
        if (!alive.current) return
        /*
          The decided row leaves the queue because it is no longer `proposed`,
          which is the same reason the server would stop sending it — not a
          cosmetic removal. The next row is selected so the analyst keeps
          working; `select` clears the form, and the impact effect fires for the
          new selection on its own.
        */
        const remaining = queue.filter((other) => other.id !== row.id)
        setLoad({ phase: 'ready', queue: remaining })
        setSelectedId(remaining[0]?.id ?? null)
        setPending(null)
        setReason('')
        setAmendType('')
        setOutcome(outcomeSentence(decision, row, result))
      })
      .catch((error: unknown) => {
        if (!alive.current) return
        // The server's own message, verbatim, for the two refusals a reviewer
        // can act on: an unconfigured building type (which enumerates the
        // declared ones) and a row somebody else has already decided.
        setProblem(error instanceof ApiError
          ? error.detail ?? error.message
          : 'That decision could not be recorded.')
        /*
          `already_reviewed` is the one refusal that is also a fact about the
          queue: somebody else decided this row, so it is no longer `proposed`
          and the server would not send it again. Rendering the message and
          leaving the row selected left it retryable forever — every retry
          answering the same 409 — which is the shape of a screen an analyst
          learns to distrust. It is dropped exactly as a decided row is, and for
          the same reason; the difference is that the message stays up, because
          it was somebody else's decision and the analyst has to be told that
          rather than watch a row vanish.

          `no_scored_book`, the other 409, is deliberately not handled here: it
          is a fact about the book rather than about this row, the decision was
          not recorded, and the row is still there to decide.
        */
        if (error instanceof ApiError && error.code === 'already_reviewed') {
          const remaining = queue.filter((other) => other.id !== row.id)
          setLoad({ phase: 'ready', queue: remaining })
          setSelectedId(remaining[0]?.id ?? null)
          setPending(null)
          setReason('')
          setAmendType('')
        }
      })
      .finally(() => {
        if (alive.current) setBusy(false)
      })
  }

  if (load.phase === 'loading') {
    return (
      <div data-testid="review-queue" data-phase="loading" style={{ padding: 24 }}>
        <p style={{ fontSize: 13.5, color: T.color.micro, margin: 0 }}>Loading the review queue…</p>
      </div>
    )
  }
  if (load.phase === 'signedOut') {
    return (
      <div data-testid="review-queue" data-phase="signed-out" style={{ padding: 24 }}>
        <p role="alert" style={{ fontSize: 13.5, color: T.color.body, margin: 0, maxWidth: '60ch' }}>
          {SESSION_ENDED}
        </p>
      </div>
    )
  }
  if (load.phase === 'forbidden') {
    return (
      <div data-testid="review-queue" data-phase="forbidden" style={{ padding: 24 }}>
        <p role="alert" style={{ fontSize: 13.5, color: T.color.body, margin: 0, maxWidth: '60ch' }}>
          {REVIEW_FORBIDDEN}
        </p>
      </div>
    )
  }
  if (load.phase === 'failed') {
    return (
      <div data-testid="review-queue" data-phase="failed" style={{ padding: 24 }}>
        <p role="alert" style={{ fontSize: 13.5, color: T.color.body, margin: 0, maxWidth: '60ch' }}>
          {QUEUE_FAILED}
        </p>
      </div>
    )
  }

  return (
    <div data-testid="review-queue" data-phase="ready" data-waiting={String(queue.length)}>
      <div
        style={{
          background: '#fff',
          borderBottom: `1.5px solid ${T.color.rule}`,
          padding: '18px 24px',
          display: 'flex',
          alignItems: 'baseline',
          gap: 14,
          flexWrap: 'wrap',
        }}
      >
        <h1 style={{ fontSize: 23, fontWeight: T.weight.bold, letterSpacing: '-0.01em', margin: 0 }}>
          {REVIEW_HEADING}
        </h1>
        {/*
          `31 awaiting` and nothing else. 1g also draws "4 amended this week";
          no route counts amendments over a window, and a figure invented in the
          browser beside a real one is the worse of the two ways to be wrong.
        */}
        <span data-testid="queue-count" style={{ ...microLabel, color: T.color.muted }}>
          {queue.length === 1 ? '1 awaiting' : `${queue.length} awaiting`}
        </span>
      </div>

      {outcome === null ? null : (
        <p
          role="status"
          data-testid="decision-outcome"
          style={{
            margin: 0,
            padding: '12px 24px',
            background: T.tint.blue,
            borderBottom: `1.5px solid ${T.color.rule}`,
            fontSize: 13,
            color: T.color.body,
          }}
        >
          {outcome}
        </p>
      )}

      {queue.length === 0
        ? (
          <p
            data-testid="queue-empty"
            style={{ margin: 0, padding: 24, fontSize: 13.5, color: T.color.body, maxWidth: '60ch' }}
          >
            {QUEUE_EMPTY}
          </p>
        )
        : (
          /*
            No `alignItems: 'start'`. The rail's right-hand rule is the spine of
            this layout, and a rail that stops at its last row leaves it hanging
            in the middle of the page beside a taller panel. Stretching is what
            makes the two columns read as one surface.
          */
          <div style={{ display: 'grid', gridTemplateColumns: '288px 1fr' }}>
            <ul
              aria-label="Proposals awaiting review"
              style={{
                margin: 0,
                padding: 0,
                listStyle: 'none',
                borderRight: `1.5px solid ${T.color.ink}`,
              }}
            >
              {queue.map((row) => {
                const current = row.id === selectedId
                return (
                  <li key={row.id}>
                    <button
                      type="button"
                      data-testid={`queue-row-${row.id}`}
                      data-selected={current ? 'true' : 'false'}
                      aria-current={current ? 'true' : undefined}
                      onClick={() => { select(row.id) }}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        border: 0,
                        borderBottom: `1px solid ${T.color.hairline}`,
                        boxShadow: current ? `inset 3px 0 0 ${T.color.red}` : 'none',
                        background: current ? T.tint.rowHover : '#fff',
                        padding: '12px 16px',
                        fontFamily: T.fontFamily,
                        color: T.color.ink,
                        cursor: 'pointer',
                      }}
                    >
                      <div style={{ fontSize: 13.5, fontWeight: T.weight.bold }}>{row.accountName}</div>
                      <div style={{ fontSize: 12.5, color: T.color.muted, marginTop: 3 }}>
                        {`Acct ${row.accountNumber} · submitted ${formatDate(row.enteredAt)}`}
                      </div>
                      <div
                        style={{
                          fontSize: 12.5,
                          marginTop: 5,
                          color: row.buildingType === 'undetermined' ? T.color.undetermined : T.color.body,
                        }}
                      >
                        {typeLabel(row.buildingType)}
                      </div>
                    </button>
                  </li>
                )
              })}
            </ul>

            {selected === null ? <div /> : (
              <div data-testid={`proposal-${selected.id}`} data-account-id={selected.accountId}>
                <section style={{ padding: 16, borderBottom: `1px solid ${T.color.hairline}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 24, flexWrap: 'wrap' }}>
                    <div>
                      <div style={microLabel}>Submitted characterization</div>
                      <div
                        data-testid="proposed-type"
                        data-building-type={selected.buildingType}
                        style={{ fontSize: 17, fontWeight: T.weight.bold, marginTop: 5 }}
                      >
                        {`Building type: ${typeLabel(selected.buildingType)}`}
                      </div>
                      <div style={{ fontSize: 12.5, color: T.color.muted, marginTop: 4 }}>
                        {`${selected.accountName} · Acct ${selected.accountNumber} · revision ${selected.version}`}
                      </div>
                    </div>
                    {/*
                      Display rule 6: who researched it, when, and from what
                      source, on the proposal the decision is about. A queue that
                      made the analyst open the account to learn where a claim
                      came from is a queue that cannot be worked.
                    */}
                    <div data-testid="provenance" style={{ textAlign: 'right', color: T.color.muted, fontSize: 12.5 }}>
                      <div style={microLabel}>Provenance</div>
                      <div style={{ marginTop: 5 }}>
                        {`${selected.enteredByName} · ${formatDate(selected.enteredAt)}`}
                      </div>
                      <div>{`Source: ${selected.researchSource}`}</div>
                    </div>
                  </div>
                  {selected.buildingType === 'undetermined'
                    ? (
                      <p
                        data-testid="undetermined-note"
                        style={{
                          margin: '12px 0 0',
                          borderLeft: `3px solid ${T.color.undetermined}`,
                          padding: '6px 0 6px 12px',
                          fontSize: 13,
                          color: T.color.body,
                          maxWidth: '70ch',
                        }}
                      >
                        {UNDETERMINED_IS_A_FINDING}
                      </p>
                    )
                    : null}
                </section>

                <section style={{ padding: 16, borderBottom: `1px solid ${T.color.hairline}` }}>
                  <div style={microLabel}>Estimate impact if approved</div>
                  {impact.phase === 'running'
                    ? (
                      <p style={{ margin: '8px 0 0', fontSize: 13, color: T.color.micro }}>
                        Scoring this account both ways…
                      </p>
                    )
                    : null}
                  {impact.phase === 'failed'
                    ? (
                      <p
                        data-testid="impact-unavailable"
                        style={{ margin: '8px 0 0', fontSize: 13, color: T.color.body, maxWidth: '70ch' }}
                      >
                        {impact.message}
                      </p>
                    )
                    : null}
                  {impact.phase === 'ready' ? <ImpactCells impact={impact.impact} /> : null}
                </section>

                <section style={{ padding: 16, borderBottom: `1px solid ${T.color.hairline}` }}>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {/*
                      Approve carries no reason and no confirmation, and that is
                      the same for `undetermined` as for a named type. `review.ts`
                      states why: "the research was right" needs no explanation,
                      and demanding one produces a queue of the word "ok".
                    */}
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => { decide('approve') }}
                      style={{ ...primaryButton, ...(busy ? { cursor: 'wait', opacity: 0.6 } : {}) }}
                    >
                      Approve &amp; re-score
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setPending(pending === 'amend' ? null : 'amend')
                        setReason('')
                        setAmendType(selected.buildingType)
                        setProblem(null)
                      }}
                      style={buttonBase}
                    >
                      Amend
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => {
                        setPending(pending === 'reject' ? null : 'reject')
                        setReason('')
                        setProblem(null)
                      }}
                      style={buttonBase}
                    >
                      Reject with reason
                    </button>
                  </div>

                  {pending === null ? null : (
                    <div
                      data-testid={pending === 'amend' ? 'amend-form' : 'reject-form'}
                      style={{ marginTop: 14, display: 'grid', gap: 10, maxWidth: 520 }}
                    >
                      {pending === 'amend'
                        ? (
                          <div>
                            <label htmlFor={typeId} style={{ ...microLabel, display: 'block', marginBottom: 5 }}>
                              Building type
                            </label>
                            <input
                              id={typeId}
                              type="text"
                              value={amendType}
                              onChange={(event) => { setAmendType(event.target.value) }}
                              style={fieldStyle}
                            />
                            {/*
                              No select, because no route serves this vocabulary
                              to an analyst — see the module docblock. The server
                              refuses an unconfigured type with a message that
                              lists the configured ones, and that message is
                              rendered below rather than paraphrased.
                            */}
                            <div style={{ fontSize: 12.5, color: T.color.muted, marginTop: 5 }}>
                              {AMENDMENT_NEEDS_A_TYPE}
                            </div>
                          </div>
                        )
                        : null}
                      <div>
                        <label htmlFor={reasonId} style={{ ...microLabel, display: 'block', marginBottom: 5 }}>
                          Reason
                        </label>
                        <textarea
                          id={reasonId}
                          value={reason}
                          rows={3}
                          onChange={(event) => { setReason(event.target.value) }}
                          style={{ ...fieldStyle, resize: 'vertical' }}
                        />
                      </div>
                      <div>
                        <button
                          type="button"
                          // `review.ts` refuses an empty reason for both verbs;
                          // this is a courtesy in front of that rule, not a
                          // second copy of it. Approve is deliberately not
                          // gated on the same state.
                          //
                          // The type is gated only for `amend`, and that one is
                          // NOT a courtesy in front of a server rule — there is
                          // no server rule. An amendment with the box cleared
                          // is accepted, carries the original type across, and
                          // reports "amended and approved as revision N" for a
                          // revision identical to the one it just rejected. The
                          // server cannot refuse it, because an amendment that
                          // changes only a footprint is legitimate; this form
                          // offers no other field, so here an empty box means
                          // an empty amendment.
                          disabled={busy || reason.trim() === ''
                            || (pending === 'amend' && amendType.trim() === '')}
                          onClick={() => { decide(pending) }}
                          style={{
                            ...primaryButton,
                            // The whole `border` shorthand, never `borderColor`
                            // on top of it: React warns when a longhand added
                            // on one render is removed on the next while the
                            // shorthand is still set, and it is right to — the
                            // two resolve in declaration order and the button
                            // keeps the wrong edge.
                            ...(reason.trim() === ''
                              || (pending === 'amend' && amendType.trim() === '')
                              ? {
                                background: T.color.chipOff,
                                border: `1.5px solid ${T.color.chipOff}`,
                                cursor: 'not-allowed',
                              }
                              : {}),
                          }}
                        >
                          {pending === 'amend' ? 'Apply amendment' : 'Confirm rejection'}
                        </button>
                      </div>
                    </div>
                  )}

                  {problem === null ? null : (
                    <p
                      role="alert"
                      data-testid="decision-problem"
                      style={{ margin: '12px 0 0', fontSize: 13, color: T.color.declinedInk, maxWidth: '70ch' }}
                    >
                      {problem}
                    </p>
                  )}
                </section>

                <p style={{ margin: 0, padding: '12px 16px 20px', fontSize: 12.5, color: T.color.muted }}>
                  {REJECTIONS_ARE_RETAINED}
                </p>
              </div>
            )}
          </div>
        )}
    </div>
  )
}
